import { getAccessToken, isSupportEnded, isSupportTab } from '@/utils/tokenStore';
import axios from 'axios';
import store from '@/store';
import { message } from 'ant-design-vue';
import { getMfaToken, stepUpPurpose, requestStepUp, geoBlockedNotice, mfaCheckUnavailableNotice } from '@/utils/mfa';
import { reportGeoBlock } from '@/utils/geoBlockReport';
import { withClientHeaders } from '@/utils/clientHeaders';
import { reloadPermsOnce, standardNotice, supportEndedReason } from '@/utils/standardErrors';
import { isSupportPagePath } from '@/utils/supportSession';


const apiInstance = axios.create({
  baseURL: process.env.VUE_APP_SERVERURL,
});

apiInstance.interceptors.request.use(
  (config) => {
    // config.headers["x-cert-from"] = process.env.VUE_APP_SALT;
    withClientHeaders(config);
    const mfaToken = getMfaToken();
    if (mfaToken) config.headers['X-Mfa-Token'] = mfaToken;
    // 지원 진입·끝 화면에서는 이 브라우저의 일반 로그인 토큰을 싣지 않음
    if (getAccessToken() && !isSupportPagePath(globalThis.location?.pathname)) {
      config.headers.Authorization = `Bearer ${getAccessToken()}`;
      config.headers['Access-Control-Allow-Origin'] = '*';
    }
    return config;
  },
  (error) => {
    // store.commit('setIsLoading', false);
    return Promise.reject(error);
}
);

let isTokenRefreshing = false;
let failedRequestsQueue = [];

apiInstance.interceptors.response.use(
    (response) => {
      // store.commit('setIsLoading', false);
      return response;
    },
    
async (error) => {
    // store.commit('setIsLoading', false);
    const originalRequest = error.config;
    // silent: 사용자가 누르지 않은 뒷단 요청(직원 권한 정기 확인 등) — 어떤 오류도 알리지 않고 그대로 실패(갱신·step-up·로그아웃 없음)
    if (originalRequest?.silent) return Promise.reject(error);
    // 국외 IP 의 모든 요청(403 GEO_BLOCKED): 안내 한 번(같은 key 라 겹치지 않음) 뒤 실패로 끝냄. step-up 은 띄우지 않음.
    // 로그인 요청은 로그인 화면 대화상자가 안내하므로 토스트를 띄우지 않음(겹침 방지).
    const geoNotice = geoBlockedNotice(error);
    if (geoNotice) {
      reportGeoBlock(originalRequest?.url === '/auth/login' ? 'LOGIN' : 'REQUEST', originalRequest);
      if (originalRequest?.url !== '/auth/login') message.warning({ content: geoNotice, key: 'GEO_BLOCKED' });
      return Promise.reject(error);
    }
    // 2FA 필요 여부 확인 실패(503 MFA_CHECK_UNAVAILABLE): 고정 문구 한 번 뒤 실패로 끝냄. 재전송·step-up 없음(F-9).
    const unavailable = mfaCheckUnavailableNotice(error);
    if (unavailable) {
      message.error({ content: unavailable, key: 'MFA_CHECK_UNAVAILABLE' });
      return Promise.reject(error);
    }
    // 증표 없음·발송 추가 인증 필요(403): step-up 창 → 통과하면 목적마다 한 번만 다시 보냄.
    // 서버가 실행 전에 막은 요청이라 쓰기 요청도 다시 보내도 중복되지 않음.
    const purpose = stepUpPurpose(error);
    if (purpose && originalRequest && !originalRequest.url?.startsWith('/auth/2fa/step-up') &&
        !originalRequest._stepUp?.includes(purpose)) {
      originalRequest._stepUp = [...(originalRequest._stepUp || []), purpose];
      if (await requestStepUp(purpose)) return apiInstance(originalRequest);
      // 사용자가 창을 닫음 → 화면은 「실패」를 띄우지 않음(mfa.alreadyNotified)
      error.stepUpCancelled = true;
      return Promise.reject(error);
    }
    // 이용 내역·권한·지원 접속의 새 오류 코드: 같은 key 로 한 번 알리고 실패로 끝냄(재전송 없음)
    const notice = standardNotice(error, { supportTab: isSupportTab(), labelOf: (code) => store.state.permLabels?.[code] });
    if (notice) {
      if (notice.text) message[notice.level]({ content: notice.text, key: notice.key });
      error.standardNotified = true; // 화면이 같은 실패를 또 띄우지 않게(mfa.alreadyNotified)
      // 지원 탭의 SUPPORT_SESSION_ENDED: 갱신하지 않고 그 탭 지원 저장값을 지운 뒤 끝 화면(사유)
      if (notice.action === 'SUPPORT_ENDED' && !isSupportEnded()) await store.dispatch('endSupport', supportEndedReason(error));
      if (notice.action === 'LOGOUT') {
        if (isSupportTab()) {
          await store.dispatch('endSupport', 'ACCOUNT_INACTIVE');
        } else {
          await store.dispatch('clearLocalStorage');
          window.location.href = '/signin';
        }
      }
      // 403 PERMISSION_DENIED: 권한(introspect)을 한 번만 다시 읽고 메뉴를 다시 그림(등록한 앱만, 반복 없음)
      if (notice.action === 'RELOAD_PERMS') reloadPermsOnce();
      return Promise.reject(error);
    }
    if(error.response && error.response.message === 'invalid ip') {
    } else {
      console.log(error)
    }
    // 끝난 지원 탭: 갱신도 재전송도 하지 않음
    if (isSupportEnded()) return Promise.reject(error);
    if(originalRequest.url && originalRequest.url !== '/auth/login') {
      if (error.response && error.response.status === 401 && !originalRequest._retry) {
        if (!isTokenRefreshing) {
          
          isTokenRefreshing = true;
          try {
            await store.dispatch('requestAccessToken');
          } catch (refreshError) {
            // 재발행 실패(폰 mobileT 실패 등): 기다리던 요청도 실패로 끝내야 앱 첫 화면이 멈추지 않는다
            failedRequestsQueue.forEach((queueItem) => queueItem.reject(error));
            failedRequestsQueue = [];
            return Promise.reject(error);
          } finally {
            isTokenRefreshing = false;
          }
          // 지원 탭이 끝났으면(래치) 원래 요청·대기 큐 모두 실패 — 재전송하지 않음(일반 로그인 토큰으로 가지 않게)
          if (isSupportEnded()) {
            failedRequestsQueue.forEach((queueItem) => queueItem.reject(error));
            failedRequestsQueue = [];
            return Promise.reject(error);
          }

          failedRequestsQueue.forEach((queueItem) => {
            queueItem.resolve(apiInstance(queueItem.originalRequest));
          });
          failedRequestsQueue = [];

          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
          return apiInstance(originalRequest);
        } else {
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({
              resolve,
              reject,
              originalRequest,
            });
          });
        }
      } 
    }
    
    return Promise.reject(error);
  }
);

export default apiInstance;